import {
  BUSINESS_LIMITS,
  type CreateReferralCodeInput,
  type PaginatedResult,
  type PanelReferralCodeDto,
  type PanelReferralCodeRedemptionDto,
  type UpdateReferralCodeInput,
} from '@icebreaker/shared';
import { api } from '@/shared/api/client';

/**
 * Referans kodlarının sayfa tavanı. Liste ucu sayfalıdır (en çok 100); sekme
 * sayaçları (Tümü / Aktif / Pasif) bütün kodları ister ve sunucuda sayaç ucu
 * yoktur. Bu yüzden sayfalar sırayla okunur ve tavan, sonsuz döngüye karşı
 * emniyettir (100 x 50 = 5.000 kod; kampanya kodu bu ölçeğe yaklaşmaz).
 */
export const REFERRAL_CODE_PAGE_SIZE = BUSINESS_LIMITS.maxPageSize;
export const REFERRAL_CODE_MAX_PAGES = 50;

/**
 * Referans kodları `api` katmanı: YALNIZCA sagadan çağrılır. Silme ucu yoktur,
 * kod yalnız pasifleşir. Düzenleme gövdesinde ad ve hediye sayısı bulunmaz
 * (sunucu bilinmeyen alanı 400 ile reddeder).
 *
 * İKİ KORUMA (tech-lead P7):
 * - Sıra `created_at DESC`tir; okuma sürerken eklenen kod sonraki sayfaları
 *   bir satır kaydırır ve aynı kod iki kez gelir. Kimliğe göre tekilleşir.
 * - Tavan dolup sunucu hâlâ `hasNext` diyorsa liste EKSİKTİR. Sessizce kesmek
 *   sekme sayaçlarını yanlış yazardı (uydurma sayı); okuma hata olarak düşer
 *   ve ekran "okunamadı" hâlini çizer.
 */
export async function fetchAllReferralCodes(): Promise<PanelReferralCodeDto[]> {
  return readAllPages<PanelReferralCodeDto>(
    (page) => api.panelReferralCodes.list({ page, pageSize: REFERRAL_CODE_PAGE_SIZE }),
    'referans kodu listesi',
  );
}

/**
 * KOD BAŞINA KULLANIMLAR (Story 6): kenar katmanı kodun BÜTÜN kullanımlarını
 * çizer (tasarımda sayfa denetimi yok). Aynı iki koruma geçerlidir: sıra
 * `redeemed_at DESC`, okuma sürerken yazılan kullanım sayfaları kaydırır ve
 * kimliğe göre tekilleşir; tavan dolarsa eksik liste çizilmez, hata hâli çizilir.
 */
export async function fetchAllReferralRedemptions(
  id: string,
): Promise<PanelReferralCodeRedemptionDto[]> {
  return readAllPages<PanelReferralCodeRedemptionDto>(
    (page) => api.panelReferralCodes.redemptions(id, { page, pageSize: REFERRAL_CODE_PAGE_SIZE }),
    'referans kodu kullanım listesi',
  );
}

async function readAllPages<T extends { readonly id: string }>(
  readPage: (page: number) => Promise<PaginatedResult<T>>,
  label: string,
): Promise<T[]> {
  const byId = new Map<string, T>();
  for (let page = 1; page <= REFERRAL_CODE_MAX_PAGES; page += 1) {
    const result = await readPage(page);
    for (const item of result.items) if (!byId.has(item.id)) byId.set(item.id, item);
    if (!result.hasNext) return [...byId.values()];
  }
  throw new Error(
    `${label} ${REFERRAL_CODE_MAX_PAGES} sayfa tavanını aştı; eksik veriyle çizilmez`,
  );
}

export async function createReferralCode(
  input: CreateReferralCodeInput,
): Promise<PanelReferralCodeDto> {
  return await api.panelReferralCodes.create(input);
}

export async function updateReferralCode(input: {
  readonly id: string;
  readonly body: UpdateReferralCodeInput;
}): Promise<PanelReferralCodeDto> {
  return await api.panelReferralCodes.update(input.id, input.body);
}
